import { cn } from '@woonstad/helpers-utils';
import clsx from 'clsx';
import { ReactElement } from 'react';

import { usesNewsTypeColors } from '../../news-type-classes';
import { Typography } from '../typography/Typography';

type Props = {
  children: ReactElement<any>
  variant?: 'landscape' | 'square';
  image: ReactElement<any>;
  publishDate?: string;
  type?: string;
  title?: string;
  intro?: string;
}

export const NewsCard = ({
  variant = 'landscape',
  image,
  children,
  type,
  title,
  intro,
  publishDate,
}: Props) => {
  const isSquare = variant === 'square';

  const typeClasses = clsx(
    'w-max',
    {
      'text-news-type-blog': type === 'Blog',
      'text-news-type-event': type === 'Evenement',
      'text-news-type-howto': type === 'Hoe werkt het',
      'text-news-type-news': usesNewsTypeColors(type),
    },
  );

  const typeBorderClasses = clsx(
    'before:absolute before:h-[calc(100%+2rem)] before:w-[1px] before:bg-[#E0E8F6] before:transition-all before:content-[""] group-hover:before:w-[3px]',
    {
      'group-hover:before:bg-news-type-blog': type === 'Blog',
      'group-hover:before:bg-news-type-event': type === 'Evenement',
      'group-hover:before:bg-news-type-howto': type === 'Hoe werkt het',
      'group-hover:before:bg-news-type-news': usesNewsTypeColors(type),
    },
  );

  const titleClasses = clsx(
    'line-clamp-3 text-news-title group-hover:underline',
    {
      'group-hover:text-news-type-blog-title-hover': type === 'Blog',
      'group-hover:text-news-type-event-title-hover': type === 'Evenement',
      'group-hover:text-news-type-howto-title-hover': type === 'Hoe werkt het',
      'group-hover:text-news-type-news-title-hover': usesNewsTypeColors(type),
    },
  );

  return (
    <children.type
      {...(children.props as any)}
      aria-label={`${title} | ${type}`}
    >
      <div
        className={clsx(
          'group flex h-full',
          isSquare && 'flex-col',
          !isSquare && 'max-h-32',
        )}
      >
        <div
          className={clsx(
            'relative aspect-[1.5/1] shrink-0 overflow-hidden',
            !isSquare && 'w-1/3',
          )}
        >
          <image.type
            {...(image.props as any)}
            className="absolute size-full object-cover"
          />
        </div>

        <div
          className="relative"
        >
          <div
            className={clsx(
              'flex',
              isSquare && 'z-10 -mt-8 flex-col-reverse',
              isSquare && typeBorderClasses,
              !isSquare && 'h-full flex-col justify-center space-y-2',
            )}
          >
            <div
              className={cn(
                'bg-news',
                isSquare && 'space-y-2 p-4',
                !isSquare && 'px-4',
              )}
            >
              <Typography
                variant="title-body"
                className={titleClasses}
              >
                {title}
              </Typography>

              {isSquare && (
                <Typography
                  variant="content-body"
                  textColorClassname="text-news-intro"
                  className="line-clamp-3"
                >
                  {intro}
                </Typography>
              )}
            </div>

            <div
              className={cn(
                'inline-block w-fit bg-news',
                isSquare && 'px-4 py-2',
                !isSquare && 'pl-4',
              )}
            >
              <div className="flex flex-wrap gap-x-2 gap-y-1">
                {publishDate ? (
                  <>
                    <Typography
                      variant="label-small"
                      type="span"
                      className="w-max text-news-date"
                    >
                      {publishDate}
                    </Typography>
                    <Typography
                      variant="label-small"
                      type="span"
                      className="text-news-date"
                    >
                      •
                    </Typography>
                  </>
                ) : null}
                <Typography
                  variant="label-small"
                  type="span"
                  className={typeClasses}
                  data-testid="newscard-type"
                >
                  {type}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>

    </children.type>
  );
};
